/**
 * AdminTributesPage — unified moderation queue across all org_sites.
 * Pending submissions show first. Approve / Delete actions per row.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Trash2, Loader2, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const AdminTributesPage = () => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin-all-tributes'],
    queryFn: () => api.get('/sites/admin-aggregate/all-tributes').then((r) => r.data),
  });

  const approveMutation = useMutation({
    mutationFn: ({ slug, id }) => api.post(`/sites/admin/${slug}/tribute/${id}/approve`),
    onSuccess: () => {
      toast.success('Tribute approved');
      queryClient.invalidateQueries({ queryKey: ['admin-all-tributes'] });
      queryClient.invalidateQueries({ queryKey: ['admin-pending-counts'] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'Approve failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: ({ slug, id }) => api.delete(`/sites/admin/${slug}/tribute/${id}`),
    onSuccess: () => {
      toast.success('Tribute deleted');
      queryClient.invalidateQueries({ queryKey: ['admin-all-tributes'] });
      queryClient.invalidateQueries({ queryKey: ['admin-pending-counts'] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'Delete failed'),
  });

  if (isLoading) {
    return (
      <div className="p-12 text-center text-slate-500">
        <Loader2 className="h-6 w-6 animate-spin inline mr-2" /> Loading tributes…
      </div>
    );
  }

  const items = data || [];
  const pending = items.filter((t) => t.status === 'pending');
  const approved = items.filter((t) => t.status !== 'pending');

  const Row = ({ t }) => (
    <div className="border rounded-lg p-4 bg-white space-y-2" data-testid={`tribute-row-${t.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-sm">{t.name}</p>
            <Badge variant="outline" className="text-xs">{t.site_name}</Badge>
            {t.imported && <Badge variant="outline" className="text-xs bg-slate-50">imported</Badge>}
            {t.status === 'pending' && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-xs">Pending</Badge>}
            {t.status === 'approved' && <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-xs">Approved</Badge>}
          </div>
          <p className="text-xs text-slate-400 mt-0.5">{t.date}</p>
          <p className="text-sm text-slate-700 mt-2 italic">&ldquo;{t.message}&rdquo;</p>
        </div>
        <div className="flex gap-2 shrink-0">
          {t.status === 'pending' && (
            <Button
              size="sm"
              onClick={() => approveMutation.mutate({ slug: t.site_slug, id: t.id })}
              disabled={approveMutation.isPending}
              data-testid={`tribute-approve-${t.id}`}
            >
              <Check className="h-4 w-4 mr-1" /> Approve
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              if (window.confirm(`Delete tribute from ${t.name}?`)) {
                deleteMutation.mutate({ slug: t.site_slug, id: t.id });
              }
            }}
            disabled={deleteMutation.isPending}
            data-testid={`tribute-delete-${t.id}`}
          >
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="admin-tributes-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <MessageSquare className="h-6 w-6 text-emerald-600" />
            Tribute Moderation
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Review and approve tribute submissions across all sites. Pending items appear first.
          </p>
        </div>
        <Button variant="outline" asChild data-testid="admin-tributes-mg-link">
          <Link to="/admin/tributes/mother-general">Manage Mother General tributes</Link>
        </Button>
      </div>

      {pending.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              Pending Review
              <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">{pending.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {pending.map((t) => <Row key={t.id} t={t} />)}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Approved <span className="text-xs text-slate-400 font-normal">({approved.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {approved.length === 0 ? (
            <p className="text-sm text-slate-400 italic">No approved tributes yet.</p>
          ) : (
            approved.slice(0, 100).map((t) => <Row key={t.id} t={t} />)
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminTributesPage;
